import { toEndOfUtcDay, toStartOfUtcDay } from '@chainflip/utils/date';
import { type UTCDate } from '@date-fns/utc';
import { format, isValid, parseISO } from 'date-fns';
import { type ParsedUrlQuery } from 'querystring';
import { localDayToUTCDay } from '@/shared/utils/date';

export type QueryParser<T> = (value: string | undefined) => T | undefined;

export const formatDateForUrl = (date: UTCDate) => format(date, 'yyyy-MM-dd');

export const parseStartDate: QueryParser<UTCDate> = (value) =>
  value && isValid(parseISO(value))
    ? toStartOfUtcDay(localDayToUTCDay(parseISO(value)))
    : undefined;

export const parseEndDate: QueryParser<UTCDate> = (value) =>
  value && isValid(parseISO(value)) ? toEndOfUtcDay(localDayToUTCDay(parseISO(value))) : undefined;

export const parseAll = <T extends string>(values: string[], definedValues: readonly T[]) => {
  const lowerCasedValues = new Set(values.map((value) => value.toLowerCase()));

  const matchedValues = definedValues.filter((value) => lowerCasedValues.has(value.toLowerCase()));

  return matchedValues.length > 0 ? matchedValues : undefined;
};

export const getQueryParams = (value: string | string[] | undefined) => {
  if (!value) return { single: undefined, commaSeparated: [] };

  const rawValue = Array.isArray(value) ? value[0] : value;
  const single = rawValue.trim() === '' ? undefined : rawValue;

  const commaSeparated = rawValue.trim()
    ? rawValue
        .split(',')
        .map((param) => param.trim())
        .filter(Boolean)
    : [];

  return { single, commaSeparated };
};

export const compareQueries = <T extends Record<string, unknown>>(a: T, b: T): boolean => {
  if (new Set(Object.keys(a)).size !== new Set(Object.keys(b)).size) return false;
  return Object.entries(a).every(([key, value]) => value === b[key]);
};

export const normalizeQuery = (query: ParsedUrlQuery): Record<string, string> =>
  Object.fromEntries(
    Object.entries(query).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(',') : String(value),
    ]),
  );
